import { Skeleton as AntdSkeleton } from 'antd';

import * as S from './styles';
import Tag from '../Tag';
import currency from 'currency.js';

export type PaymentBoxProps = {
  monthlyPayment: number;
  halfPayment: number;
  term: number;
  paymentOption: 'full' | 'half';
  weeklyCycle: boolean;
  weekTerm: number;
  weeklyPayment: number;
  downPayment?: number;
  firstPayment?: number;
  children?: React.ReactNode;
  loading?: boolean;
};

const Skeleton = () => {
  return (
    <S.SkeletonContainer aria-label="Loading data">
      <AntdSkeleton.Input active key="skeleton-0" />
      {Array.from({ length: 6 }).map((_, index) => (
        <AntdSkeleton.Input
          active
          key={`skeleton-${index + 1}`}
          style={{ width: '100%' }}
        />
      ))}
    </S.SkeletonContainer>
  );
};

const today = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();

  return `${dd}/${mm}/${yyyy}`;
}

const PaymentBox = ({
  halfPayment,
  monthlyPayment,
  paymentOption,
  term,
  weeklyCycle,
  weekTerm,
  weeklyPayment,
  children,
  downPayment = 0,
  firstPayment,
  loading,
}: PaymentBoxProps) => {
  let paymentAmount = monthlyPayment;
  if (paymentOption === 'half') {
    paymentAmount = halfPayment;
  } else if (weeklyCycle) {
    paymentAmount = weeklyPayment;
  }

  const hasFirstPaymentDiscount = !!firstPayment;
  const firstInstallment = hasFirstPaymentDiscount ? firstPayment : paymentAmount;
  const todayTotal = firstInstallment + downPayment;

  return (
    <S.Wrapper>
      {loading ? (
        <Skeleton />
      ) : (
        <>
          <S.Row>
            <S.FirstPaymentSection>
              <S.FirstPayment>
                <S.Label>You will pay today</S.Label>
                <S.FirstPaymentValue aria-label="You will pay today value">
                  {currency(todayTotal).format()}{weeklyCycle ? '/wk' : '/mo'}
                </S.FirstPaymentValue>
              </S.FirstPayment>
              <Tag variant="secondary">{paymentOption} payment activated</Tag>
            </S.FirstPaymentSection>
          </S.Row>

          {!!downPayment && (
            <S.Row>
              <S.DetailsSection>
                <S.DetailsTitle>Details</S.DetailsTitle>
                <S.DetailsText aria-label="First Payment Details">
                  {currency(firstInstallment).format()}
                  <span>
                    (
                    {paymentOption === 'half'
                      ? 'Until Credit Voucher'
                      : 'First Payment'}
                    )
                  </span>{' '}
                  {!!downPayment && (
                    <>
                      + {currency(downPayment).format()}{' '}
                      <span>(Down Payment)</span>{' '}
                    </>
                  )}
                  = {currency(firstInstallment + downPayment).format()}
                </S.DetailsText>
                {paymentOption === 'half' && (
                  <S.AfterCreditVoucher>
                    <S.DetailsText>
                      <span>After Credit Voucher =</span>{' '}
                      <strong aria-label="After credit voucher value">
                        {currency(monthlyPayment).format()}/mo
                      </strong>
                    </S.DetailsText>
                  </S.AfterCreditVoucher>
                )}
              </S.DetailsSection>
            </S.Row>
          )}

          <S.Row>
            <S.Info>
              <S.Item>
                <S.Text>Loan term</S.Text>
                <S.Text color="neutralSilver80" aria-label="Loan term">
                  {(weeklyCycle ? `${weekTerm} weeks` : `${term} months`)}
                </S.Text>
              </S.Item>
              <S.Item>
                <S.Text>
                  {weeklyCycle ? 'Weekly' : 'Monthly'} due
                  {hasFirstPaymentDiscount ? ' (2nd+)' : ''}
                </S.Text>
                <S.Text color="neutralSilver80" aria-label="Monthly due">
                  {currency(paymentAmount).format()}
                  {paymentOption === 'half' && (
                    <>
                      <span> (Until Credit Voucher)</span>
                    </>
                  )}
                </S.Text>
              </S.Item>
              {paymentOption === 'half' && !downPayment && (
                <S.Item>
                  <S.Text>{weeklyCycle ? 'Weekly' : 'Monthly'} due</S.Text>
                  <S.Text
                    color="neutralSilver80"
                    aria-label={`${weeklyCycle ? 'Weekly' : 'Monthly'} due after credit voucher`}
                  >
                    {currency(weeklyCycle ? weeklyPayment : monthlyPayment).format()}
                    <span> (After Credit Voucher)</span>
                  </S.Text>
                </S.Item>
              )}
              <S.Item>
                <S.Text>Due on</S.Text>
                <S.Text color="neutralSilver80" aria-label="Due on">
                  {(weeklyCycle) ? `${today()}` : `${new Date().getDate()}th monthly`}
                </S.Text>
              </S.Item>
            </S.Info>
          </S.Row>

          {paymentOption === 'half' && (
            <S.Row>
              <S.Disclaimer>
                Term and conditions subject to change with half-payment plans
              </S.Disclaimer>
            </S.Row>
          )}

          {children}
        </>
      )}
    </S.Wrapper>
  );
};

export default PaymentBox;
